import React from "react";
import {
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  featureCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  radioLabel: {
    marginBottom: theme.spacing(0.5),
  },
  description: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(1),
  },
}));

/**
 * OneOf component renders a radio group for mutually exclusive feature options.
 *
 * @param {Object} props
 * @param {string} props.title - Title displayed above the radio group
 * @param {Array} props.options - Array of option objects with { value, label, description, feature }
 *   - value: the radio button value
 *   - label: display label for the option
 *   - description: optional description shown below the option
 *   - feature: optional feature flag name to add when selected (if not default)
 * @param {string} props.defaultValue - The default value when no feature flag is set
 * @param {Array} props.features - Current features array
 * @param {Function} props.setFeatures - Function to update features
 * @param {boolean} props.disabled - Whether the radio group is disabled
 */
export const OneOf = ({
  title,
  options,
  defaultValue,
  features,
  setFeatures,
  disabled = false,
}) => {
  const classes = useStyles();

  // Determine current value based on which feature flag is present
  const getCurrentValue = () => {
    for (const option of options) {
      if (option.feature && features.includes(option.feature)) {
        return option.value;
      }
    }
    return defaultValue;
  };

  const currentValue = getCurrentValue();

  const handleChange = (event) => {
    if (disabled) return;

    const newValue = event.target.value;
    const selectedOption = options.find((opt) => opt.value === newValue);

    // Remove all feature flags from this OneOf group
    let newFeatures = features.filter(
      (f) => !options.some((opt) => opt.feature === f)
    );

    // Add the new feature flag if it's not the default
    if (selectedOption?.feature) {
      newFeatures = [...newFeatures, selectedOption.feature];
    }

    setFeatures(newFeatures);
  };

  return (
    <Paper
      className={classes.featureCard}
      elevation={1}
    >
      <Typography variant="subtitle1" className={classes.title}>
        {title}
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup value={currentValue} onChange={handleChange}>
          {options.map((option) => (
            <React.Fragment key={option.value}>
              <FormControlLabel
                value={option.value}
                control={<Radio color="primary" size="small" />}
                label={option.label}
                className={classes.radioLabel}
                disabled={disabled}
              />
              {option.description && (
                <Typography variant="body2" className={classes.description}>
                  {option.description}
                </Typography>
              )}
            </React.Fragment>
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  );
};
